export type Finish = "standard" | "goldFoil" | "holographic";
export type AlphaMode = "opaque" | "straight" | "mask";

export interface CardAsset {
  assetId: string;
  role: string;
  mimeType: "image/jpeg" | "image/png" | "image/webp";
  pixelWidth: number;
  pixelHeight: number;
  byteSize: number;
  sha256: string;
  alphaMode?: AlphaMode;
}

interface CardRender {
  finish: Finish;
  intensity: number;
  roughness: number;
  reflection: number;
  parallax: number;
  hueShift: number;
  flipAxis: "vertical";
}

interface ManifestBase {
  rendererVersion: string;
  templateVersion: string;
  aspectRatio: { width: 5; height: 7 };
  coordinateSystem: { origin: "topLeft"; uAxis: "right"; vAxis: "down" };
  colorSpace: "sRGB";
  render: CardRender;
}

interface LegacyFace {
  base: CardAsset;
  preview: CardAsset;
  foilMask?: CardAsset;
  depthMask?: CardAsset;
}

export interface CardManifestV1 extends ManifestBase {
  schemaVersion: 1;
  front: LegacyFace;
  back: LegacyFace;
}

export type EffectColor = [number, number, number];
export type EffectVector = [number, number];

export interface EffectEmitter {
  source: "background" | "subject" | "subjectContour" | "lineart" | "foilMask" | "fullCard";
  shape: "dust" | "spark" | "streak" | "ring" | "shard" | "atlas";
  blendMode: "add" | "screen";
  palette: EffectColor[];
  density: number;
  size: number;
  speed: number;
  turbulence: number;
  drift: EffectVector;
  twinkle: number;
  tiltCoupling: number;
  depth: number;
  opacity: number;
}

export interface EffectRecipe {
  recipeVersion: 1;
  cardSeed: number;
  sessionVariation: number;
  composition: {
    subjectScale: number;
    subjectOffset: EffectVector;
    subjectDepth: number;
    backgroundDepth: number;
  };
  foil: {
    field: "linearWave" | "radialWave" | "vortex" | "cellular" | "interference";
    blendMode: "overlay" | "screen" | "softLight";
    palette: EffectColor[];
    frequency: number;
    rotation: number;
    warp: number;
    sweepWidth: number;
    tiltCoupling: number;
    intensity: number;
  };
  glow: {
    intensity: number;
    width: number;
    pulseRate: number;
    tiltCoupling: number;
    color: EffectColor;
  };
  emitters: EffectEmitter[];
  post: {
    bloom: number;
    bloomRadius: number;
    vignette: number;
    grain: number;
    targetFPS: 30 | 60 | 120;
  };
}

export interface CardManifestV2 extends ManifestBase {
  schemaVersion: 2;
  front: {
    base: CardAsset;
    preview: CardAsset;
    background: CardAsset;
    subject: CardAsset;
    lineart: CardAsset;
    typography: CardAsset;
    foilMask?: CardAsset;
    exclusionMask?: CardAsset;
    particleAtlas?: CardAsset;
  };
  back: { base: CardAsset; preview: CardAsset };
  effectRecipe: EffectRecipe;
}

export type CardManifest = CardManifestV1 | CardManifestV2;

export interface SharedCard {
  id: string;
  title: string;
  story: string;
  displayNumber: string;
  revisionId: string;
  revision: CardManifest;
  sharedAt: string;
  openAppURL: string;
}

export function isShareToken(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{32,128}$/u.test(value);
}

export function parseSharedCard(value: unknown, expectedToken?: string): SharedCard | undefined {
  const card = record(value);
  const revision = card?.revision;
  if (!card || !isUUID(card.id) || !boundedString(card.title, 80, true)
      || !boundedString(card.story, 2_000) || !boundedString(card.displayNumber, 32, true)
      || !isUUID(card.revisionId) || !validDate(card.sharedAt)
      || !validOpenAppURL(card.openAppURL, expectedToken) || !isCardManifest(revision)) return undefined;
  return {
    id: card.id,
    title: card.title,
    story: card.story,
    displayNumber: card.displayNumber,
    revisionId: card.revisionId,
    revision: publicManifest(revision),
    sharedAt: card.sharedAt,
    openAppURL: card.openAppURL,
  };
}

export function supportsInteractiveRenderer(manifest: CardManifest): boolean {
  return (manifest.schemaVersion === 1 && manifest.rendererVersion.startsWith("1."))
    || (manifest.schemaVersion === 2 && manifest.rendererVersion.startsWith("2."));
}

function isCardManifest(value: unknown): value is CardManifest {
  const manifest = record(value);
  if (!manifest || !validManifestBase(manifest)) return false;
  if (manifest.schemaVersion === 1) return isLegacyManifest(manifest);
  if (manifest.schemaVersion === 2) return isLayeredManifest(manifest);
  return false;
}

function validManifestBase(manifest: Record<string, unknown>): boolean {
  const aspectRatio = record(manifest.aspectRatio);
  const coordinateSystem = record(manifest.coordinateSystem);
  const render = record(manifest.render);
  return boundedString(manifest.rendererVersion, 64, true)
    && /^\d+\.\d+\.\d+$/u.test(manifest.rendererVersion as string)
    && boundedString(manifest.templateVersion, 64, true)
    && Boolean(aspectRatio) && aspectRatio?.width === 5 && aspectRatio.height === 7
    && Boolean(coordinateSystem) && coordinateSystem?.origin === "topLeft"
    && coordinateSystem.uAxis === "right" && coordinateSystem.vAxis === "down"
    && manifest.colorSpace === "sRGB" && Boolean(render)
    && ["standard", "goldFoil", "holographic"].includes(String(render?.finish))
    && boundedNumber(render?.intensity, 0, 1) && boundedNumber(render?.roughness, 0, 1)
    && boundedNumber(render?.reflection, 0, 1) && boundedNumber(render?.parallax, 0, 0.08)
    && boundedNumber(render?.hueShift, -180, 180) && render?.flipAxis === "vertical";
}

function isLegacyManifest(manifest: Record<string, unknown>): boolean {
  if (!(manifest.rendererVersion as string).startsWith("1.") || "effectRecipe" in manifest) return false;
  const front = record(manifest.front);
  const back = record(manifest.back);
  if (!front || !back
      || !validAsset(front.base, "frontBase") || !validAsset(front.preview, "frontPreview")
      || !validOptionalAsset(front.foilMask, "frontFoilMask") || !validOptionalAsset(front.depthMask, "frontDepthMask")
      || !validAsset(back.base, "backBase") || !validAsset(back.preview, "backPreview")
      || !validOptionalAsset(back.foilMask, "backFoilMask") || !validOptionalAsset(back.depthMask, "backDepthMask")) return false;
  return uniqueAssets([front.base, front.preview, front.foilMask, front.depthMask, back.base, back.preview, back.foilMask, back.depthMask]);
}

function isLayeredManifest(manifest: Record<string, unknown>): boolean {
  if (!(manifest.rendererVersion as string).startsWith("2.") || !validEffectRecipe(manifest.effectRecipe)) return false;
  const front = record(manifest.front);
  const back = record(manifest.back);
  if (!front || !back
      || !validAsset(front.base, "frontBase", "opaque") || !validAsset(front.preview, "frontPreview", "opaque")
      || !validAsset(front.background, "frontBackground", "opaque")
      || !validAsset(front.subject, "frontSubject", "straight")
      || !validAsset(front.lineart, "frontLineart", "mask")
      || !validAsset(front.typography, "frontTypography", "straight")
      || !validOptionalAsset(front.foilMask, "frontFoilMask", "mask")
      || !validOptionalAsset(front.exclusionMask, "frontExclusionMask", "mask")
      || !validOptionalAsset(front.particleAtlas, "frontParticleAtlas", "straight", false)
      || !validAsset(back.base, "backBase", "opaque") || !validAsset(back.preview, "backPreview", "opaque")) return false;
  const recipe = manifest.effectRecipe as EffectRecipe;
  if (recipe.emitters.some((emitter) => emitter.shape === "atlas") && !front.particleAtlas) return false;
  if (recipe.emitters.some((emitter) => emitter.source === "foilMask") && !front.foilMask) return false;
  return uniqueAssets([
    front.base, front.preview, front.background, front.subject, front.lineart, front.typography,
    front.foilMask, front.exclusionMask, front.particleAtlas, back.base, back.preview,
  ]);
}

function validEffectRecipe(value: unknown): value is EffectRecipe {
  const recipe = record(value);
  const composition = record(recipe?.composition);
  const foil = record(recipe?.foil);
  const glow = record(recipe?.glow);
  const post = record(recipe?.post);
  if (!recipe || !hasOnlyKeys(recipe, ["recipeVersion", "cardSeed", "sessionVariation", "composition", "foil", "glow", "emitters", "post"])
      || recipe.recipeVersion !== 1 || !Number.isInteger(recipe.cardSeed)
      || !boundedNumber(recipe.cardSeed, 0, 4_294_967_295) || !boundedNumber(recipe.sessionVariation, 0, 1)
      || !composition || !hasOnlyKeys(composition, ["subjectScale", "subjectOffset", "subjectDepth", "backgroundDepth"])
      || !boundedNumber(composition.subjectScale, 0.8, 1.6)
      || !validVector(composition.subjectOffset) || !boundedNumber(composition.subjectDepth, 0, 0.08)
      || !boundedNumber(composition.backgroundDepth, -0.08, 0)
      || !foil || !hasOnlyKeys(foil, ["field", "blendMode", "palette", "frequency", "rotation", "warp", "sweepWidth", "tiltCoupling", "intensity"])
      || !["linearWave", "radialWave", "vortex", "cellular", "interference"].includes(String(foil.field))
      || !["overlay", "screen", "softLight"].includes(String(foil.blendMode)) || !validPalette(foil.palette)
      || !boundedNumber(foil.frequency, 0.2, 24) || !boundedNumber(foil.rotation, -Math.PI, Math.PI)
      || !boundedNumber(foil.warp, 0, 1) || !boundedNumber(foil.sweepWidth, 0.01, 0.5)
      || !boundedNumber(foil.tiltCoupling, 0, 1) || !boundedNumber(foil.intensity, 0, 1)
      || !glow || !hasOnlyKeys(glow, ["intensity", "width", "pulseRate", "tiltCoupling", "color"])
      || !boundedNumber(glow.intensity, 0, 1) || !boundedNumber(glow.width, 0.001, 0.08)
      || !boundedNumber(glow.pulseRate, 0, 4) || !boundedNumber(glow.tiltCoupling, 0, 1) || !validColor(glow.color)
      || !Array.isArray(recipe.emitters) || recipe.emitters.length > 3 || !recipe.emitters.every(validEmitter)
      || !post || !hasOnlyKeys(post, ["bloom", "bloomRadius", "vignette", "grain", "targetFPS"])
      || !boundedNumber(post.bloom, 0, 0.65) || !boundedNumber(post.bloomRadius, 0.05, 1)
      || !boundedNumber(post.vignette, 0, 0.35) || !boundedNumber(post.grain, 0, 0.12)
      || !Number.isInteger(post.targetFPS) || ![30, 60, 120].includes(post.targetFPS as number)) return false;
  return true;
}

function validEmitter(value: unknown): value is EffectEmitter {
  const emitter = record(value);
  return Boolean(emitter)
    && hasOnlyKeys(emitter!, ["source", "shape", "blendMode", "palette", "density", "size", "speed", "turbulence", "drift", "twinkle", "tiltCoupling", "depth", "opacity"])
    && ["background", "subject", "subjectContour", "lineart", "foilMask", "fullCard"].includes(String(emitter?.source))
    && ["dust", "spark", "streak", "ring", "shard", "atlas"].includes(String(emitter?.shape))
    && ["add", "screen"].includes(String(emitter?.blendMode)) && validPalette(emitter?.palette)
    && boundedNumber(emitter?.density, 0, 1) && boundedNumber(emitter?.size, 0.1, 3)
    && boundedNumber(emitter?.speed, 0, 2) && boundedNumber(emitter?.turbulence, 0, 1)
    && validVector(emitter?.drift) && boundedNumber(emitter?.twinkle, 0, 1)
    && boundedNumber(emitter?.tiltCoupling, 0, 1) && boundedNumber(emitter?.depth, -0.08, 0.08)
    && boundedNumber(emitter?.opacity, 0, 1);
}

function validPalette(value: unknown): value is EffectColor[] {
  return Array.isArray(value) && value.length >= 2 && value.length <= 4 && value.every(validColor);
}

function validColor(value: unknown): value is EffectColor {
  return Array.isArray(value) && value.length === 3 && value.every((part) => boundedNumber(part, 0, 1));
}

function validVector(value: unknown): value is EffectVector {
  return Array.isArray(value) && value.length === 2 && value.every((part) => boundedNumber(part, -1, 1));
}

function validAsset(value: unknown, role: string, alphaMode?: AlphaMode, requiresCardAspect = true): value is CardAsset {
  const asset = record(value);
  if (!asset || !isUUID(asset.assetId) || asset.role !== role
      || !["image/jpeg", "image/png", "image/webp"].includes(String(asset.mimeType))
      || !Number.isInteger(asset.pixelWidth) || !Number.isInteger(asset.pixelHeight)
      || Number(asset.pixelWidth) < 1 || Number(asset.pixelWidth) > 8_192
      || Number(asset.pixelHeight) < 1 || Number(asset.pixelHeight) > 8_192
      || (requiresCardAspect && Number(asset.pixelWidth) * 7 !== Number(asset.pixelHeight) * 5)
      || (alphaMode === undefined ? asset.alphaMode !== undefined : asset.alphaMode !== alphaMode)
      || !Number.isInteger(asset.byteSize) || Number(asset.byteSize) < 1 || Number(asset.byteSize) > 32 * 1_024 * 1_024
      || typeof asset.sha256 !== "string" || !/^[a-f0-9]{64}$/u.test(asset.sha256)) return false;
  return true;
}

function validOptionalAsset(value: unknown, role: string, alphaMode?: AlphaMode, requiresCardAspect = true): boolean {
  return value === undefined || validAsset(value, role, alphaMode, requiresCardAspect);
}

function uniqueAssets(values: unknown[]): boolean {
  const assets = values.filter((asset): asset is CardAsset => asset !== undefined);
  return new Set(assets.map((asset) => asset.assetId)).size === assets.length;
}

function publicManifest(manifest: CardManifest): CardManifest {
  if (manifest.schemaVersion === 1) {
    return {
      ...publicManifestBase(manifest),
      schemaVersion: 1,
      front: publicLegacyFace(manifest.front),
      back: publicLegacyFace(manifest.back),
    };
  }
  return {
    ...publicManifestBase(manifest),
    schemaVersion: 2,
    front: {
      base: publicAsset(manifest.front.base),
      preview: publicAsset(manifest.front.preview),
      background: publicAsset(manifest.front.background),
      subject: publicAsset(manifest.front.subject),
      lineart: publicAsset(manifest.front.lineart),
      typography: publicAsset(manifest.front.typography),
      ...(manifest.front.foilMask ? { foilMask: publicAsset(manifest.front.foilMask) } : {}),
      ...(manifest.front.exclusionMask ? { exclusionMask: publicAsset(manifest.front.exclusionMask) } : {}),
      ...(manifest.front.particleAtlas ? { particleAtlas: publicAsset(manifest.front.particleAtlas) } : {}),
    },
    back: { base: publicAsset(manifest.back.base), preview: publicAsset(manifest.back.preview) },
    effectRecipe: publicEffectRecipe(manifest.effectRecipe),
  };
}

function publicEffectRecipe(recipe: EffectRecipe): EffectRecipe {
  return {
    recipeVersion: 1,
    cardSeed: recipe.cardSeed,
    sessionVariation: recipe.sessionVariation,
    composition: {
      subjectScale: recipe.composition.subjectScale,
      subjectOffset: copyVector(recipe.composition.subjectOffset),
      subjectDepth: recipe.composition.subjectDepth,
      backgroundDepth: recipe.composition.backgroundDepth,
    },
    foil: {
      field: recipe.foil.field,
      blendMode: recipe.foil.blendMode,
      palette: recipe.foil.palette.map(copyColor),
      frequency: recipe.foil.frequency,
      rotation: recipe.foil.rotation,
      warp: recipe.foil.warp,
      sweepWidth: recipe.foil.sweepWidth,
      tiltCoupling: recipe.foil.tiltCoupling,
      intensity: recipe.foil.intensity,
    },
    glow: {
      intensity: recipe.glow.intensity,
      width: recipe.glow.width,
      pulseRate: recipe.glow.pulseRate,
      tiltCoupling: recipe.glow.tiltCoupling,
      color: copyColor(recipe.glow.color),
    },
    emitters: recipe.emitters.map((emitter) => ({
      source: emitter.source,
      shape: emitter.shape,
      blendMode: emitter.blendMode,
      palette: emitter.palette.map(copyColor),
      density: emitter.density,
      size: emitter.size,
      speed: emitter.speed,
      turbulence: emitter.turbulence,
      drift: copyVector(emitter.drift),
      twinkle: emitter.twinkle,
      tiltCoupling: emitter.tiltCoupling,
      depth: emitter.depth,
      opacity: emitter.opacity,
    })),
    post: {
      bloom: recipe.post.bloom,
      bloomRadius: recipe.post.bloomRadius,
      vignette: recipe.post.vignette,
      grain: recipe.post.grain,
      targetFPS: recipe.post.targetFPS,
    },
  };
}

function publicManifestBase(manifest: CardManifest): ManifestBase {
  return {
    rendererVersion: manifest.rendererVersion,
    templateVersion: manifest.templateVersion,
    aspectRatio: { width: 5, height: 7 },
    coordinateSystem: { origin: "topLeft", uAxis: "right", vAxis: "down" },
    colorSpace: "sRGB",
    render: {
      finish: manifest.render.finish,
      intensity: manifest.render.intensity,
      roughness: manifest.render.roughness,
      reflection: manifest.render.reflection,
      parallax: manifest.render.parallax,
      hueShift: manifest.render.hueShift,
      flipAxis: "vertical",
    },
  };
}

function publicLegacyFace(face: LegacyFace): LegacyFace {
  return {
    base: publicAsset(face.base),
    preview: publicAsset(face.preview),
    ...(face.foilMask ? { foilMask: publicAsset(face.foilMask) } : {}),
    ...(face.depthMask ? { depthMask: publicAsset(face.depthMask) } : {}),
  };
}

function publicAsset(asset: CardAsset): CardAsset {
  return {
    assetId: asset.assetId,
    role: asset.role,
    mimeType: asset.mimeType,
    pixelWidth: asset.pixelWidth,
    pixelHeight: asset.pixelHeight,
    byteSize: asset.byteSize,
    sha256: asset.sha256,
    ...(asset.alphaMode ? { alphaMode: asset.alphaMode } : {}),
  };
}

function boundedString(value: unknown, maximum: number, requireContent = false): value is string {
  return typeof value === "string" && value.length <= maximum && (!requireContent || value.length > 0);
}

function boundedNumber(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= minimum && value <= maximum;
}

function copyColor(value: EffectColor): EffectColor {
  return [value[0], value[1], value[2]];
}

function copyVector(value: EffectVector): EffectVector {
  return [value[0], value[1]];
}

function validDate(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 40) return false;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString() === value;
}

function validOpenAppURL(value: unknown, expectedToken?: string): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    const path = url.pathname.match(/^\/c\/([A-Za-z0-9_-]{32,128})$/u);
    const environment = url.searchParams.get("environment");
    return url.protocol === "https:"
      && url.hostname === "lorefoil.kifferliu.com"
      && url.port === "" && url.username === "" && url.password === "" && url.hash === ""
      && Boolean(path) && (expectedToken === undefined || path?.[1] === expectedToken)
      && [...url.searchParams.keys()].every((key) => key === "environment")
      && [...url.searchParams.keys()].length <= 1
      && (environment === null || environment === "staging" || environment === "development");
  } catch { return false; }
}

export function trustedAPIBaseURL(value: unknown, allowLoopback = false): string | undefined {
  if (typeof value !== "string" || value.length === 0 || value.length > 2_048 || value !== value.trim()) return undefined;
  try {
    const url = new URL(value);
    const isProductionHost = ["api.lorefoil.kifferliu.com", "staging-api.lorefoil.kifferliu.com"].includes(url.hostname);
    const isLoopback = allowLoopback && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
    if ((!isProductionHost && !isLoopback)
        || (isProductionHost && (url.protocol !== "https:" || url.port !== ""))
        || (isLoopback && !["http:", "https:"].includes(url.protocol))
        || url.username !== "" || url.password !== "" || url.pathname !== "/"
        || url.search !== "" || url.hash !== "") return undefined;
    return url.origin;
  } catch { return undefined; }
}

function isUUID(value: unknown): value is string {
  return typeof value === "string"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value);
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: string[]): boolean {
  const allowList = new Set(allowed);
  return Object.keys(value).every((key) => allowList.has(key));
}
